import { Link } from "react-router-dom";
import { ArrowLeft, Zap } from "lucide-react";
import { BRAND } from "@/lib/constants";
import { tweaksByCategory } from "@/lib/tweaksContent";
import { useSeo } from "@/lib/seo";

const RISK_STYLES = {
  low: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  medium: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  high: "bg-red-500/10 text-red-400 border-red-500/30",
};

const RISK_LABELS = { low: "Low Risk", medium: "Medium Risk", high: "High Risk" };

const slug = (category) => category;

const TweakCard = ({ tweak }) => (
  <div className="border border-white/5 bg-white/[0.02] p-5" data-testid={`doc-tweak-${tweak.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
      <h3 className="font-black uppercase tracking-tight text-white text-lg" style={{ fontFamily: "'Orbitron', sans-serif" }}>
        {tweak.name}
      </h3>
      <span className={`shrink-0 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] ${RISK_STYLES[tweak.riskLevel]}`}>
        {RISK_LABELS[tweak.riskLevel]}
      </span>
    </div>
    <p className="text-zinc-300 text-sm leading-relaxed mb-2">{tweak.whatItChanges}</p>
    <p className="text-zinc-500 text-sm leading-relaxed">{tweak.whyItMayHelp}</p>
    <div className="mt-4 flex flex-wrap gap-4 text-[11px] uppercase tracking-[0.15em] text-zinc-600">
      <span>{tweak.requiresRestart ? "Requires restart" : "No restart needed"}</span>
      <span>{tweak.reversible ? "Individually reversible" : "Not individually reversible"}</span>
    </div>
  </div>
);

export default function Documentation() {
  useSeo({
    title: "Documentation — Flow Tweaks",
    description:
      "Every tweak Flow Tweaks applies, what it changes on your system, and how to reverse it.",
    path: "/documentation",
  });

  const groups = tweaksByCategory();

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="documentation-page">
      <header className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="docs-home-link">
            <Zap className="w-5 h-5 text-orange-500 fill-orange-500" />
            <span className="font-black tracking-[0.18em] text-sm uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              {BRAND.name}
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-orange-500 transition-colors"
            data-testid="docs-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <p className="text-orange-500 font-bold tracking-[0.3em] text-xs uppercase mb-4">Documentation</p>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter mb-4 leading-[0.95]"
          style={{ fontFamily: "'Orbitron', sans-serif" }}
        >
          Every tweak, <span className="text-orange-500">explained.</span>
        </h1>
        <p className="text-zinc-400 text-base sm:text-lg leading-relaxed max-w-2xl mb-10">
          Every optimization Flow Tweaks can apply — what it actually changes, why it may help, and
          how risky it is. Nothing here is applied automatically; the app always scans your PC first
          and only touches what you choose.
        </p>

        <div className="mb-16 border border-orange-500/20 bg-orange-500/[0.03] p-5">
          <p className="text-zinc-400 text-sm leading-relaxed">
            Results depend entirely on your hardware and existing configuration — we don&apos;t
            promise a specific FPS number for any tweak, here or in the app. Risk level reflects
            how contained and reversible a change is, not how likely it is to help.
          </p>
        </div>

        <nav className="mb-16 flex flex-wrap gap-2" aria-label="Jump to category" data-testid="docs-category-nav">
          {groups.map(({ category, label }) => (
            <a
              key={category}
              href={`#${slug(category)}`}
              className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-400 hover:text-orange-500 border border-white/10 hover:border-orange-500/40 rounded-full px-4 py-2 transition-colors"
            >
              {label}
            </a>
          ))}
        </nav>

        {groups.map(({ category, label, tweaks }) => (
          <section key={category} id={slug(category)} className="mb-16 scroll-mt-24">
            <h2
              className="text-2xl sm:text-3xl font-black uppercase tracking-tight mb-6"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              <span className="text-orange-500 mr-3">/</span>
              {label}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {tweaks.map((tweak) => (
                <TweakCard key={tweak.id} tweak={tweak} />
              ))}
            </div>
          </section>
        ))}

        <div className="mt-4 pt-8 border-t border-white/5 text-xs tracking-[0.2em] uppercase text-zinc-600">
          © {new Date().getFullYear()} Flow Tweaks. All rights reserved.
        </div>
      </main>
    </div>
  );
}
